import { changePassword } from "@/api";
import { useAuth } from "@/contexts/auth/useAuth";
import { parseAuthError } from "@/utils/auth-errors";
import { Box } from "@mui/material";
import { FormContainer, FormPasswordElement } from "@rhf-kit/mui";
import { useMutation } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Button } from "../../buttons/Button";

const ChangePasswordFormElements = ({
    isLoading,
    reset,
}: {
    isLoading: boolean;
    reset: boolean;
}) => {
    const { reset: resetForm } = useFormContext();

    useEffect(() => {
        resetForm();
    }, [reset, resetForm]);

    return (
        <>
            <FormPasswordElement
                id="currentPassword"
                fullWidth
                name="currentPassword"
                label="Current Password"
                margin="normal"
                required
            />
            <FormPasswordElement
                id="newPassword"
                fullWidth
                name="newPassword"
                label="New Password"
                margin="normal"
                required
            />
            <FormPasswordElement
                id="confirmNewPassword"
                fullWidth
                name="confirmNewPassword"
                label="Confirm New Password"
                margin="normal"
                required
                rules={{
                    required: "Confirm New Password is required",
                    validate: (value, formValues) =>
                        value === formValues.newPassword ||
                        "Passwords do not match",
                }}
            />
            <Box mt={2} display="flex">
                <Box flexGrow={1} />
                <Button
                    loading={isLoading}
                    variant="contained"
                    color="dark"
                    type="submit"
                    sx={{ borderRadius: 2 }}
                >
                    Save Changes
                </Button>
            </Box>
        </>
    );
};

interface FormData {
    currentPassword: string;
    newPassword: string;
    confirmNewPassword: string;
}

export const ChangePasswordForm = () => {
    const { user } = useAuth();

    // Used to trigger the reset() within the nested component
    const [reset, setReset] = useState(false);

    const passwordMutation = useMutation({
        mutationFn: changePassword,
        onSuccess: () => {
            enqueueSnackbar({
                variant: "success",
                message: "Password successfully updated",
            });
            setReset(!reset);
        },
        onError: (error: unknown) => {
            const { message } = parseAuthError(error);
            enqueueSnackbar({
                variant: "error",
                message: message,
            });
        },
    });

    const handleSubmit = async (formData: FormData) => {
        passwordMutation.mutate({
            current_password: formData.currentPassword,
            new_password: formData.newPassword,
        });
    };

    if (!user) {
        return null;
    }

    return (
        <FormContainer
            defaultValues={{
                currentPassword: "",
                newPassword: "",
                confirmNewPassword: "",
            }}
            onSuccess={handleSubmit}
        >
            <ChangePasswordFormElements
                isLoading={passwordMutation.isPending}
                reset={reset}
            />
        </FormContainer>
    );
};
